/**
 * DataRecoveryPage — Owner-only list of past reset batches with 1-click restore.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowCounterClockwise, ArrowClockwise, Warning, CheckCircle, Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

const TYPE_LABELS = {
  distributor: 'Distributor',
  card: 'Card Vendor',
  gift_card: 'Gift Card Vendor',
  recharge: 'Recharge Vendor',
  electricity: 'Electricity Vendor',
};

export default function DataRecoveryPage() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | active | restored

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/data-reset-access/me');
        setMeta(r.data);
      } catch { setMeta({ can_access: false }); }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const r = await api.get(`/data-reset/batches${params}`);
      setBatches(r.data || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('Access required');
      else toast.error('Load failed');
      setBatches([]);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (meta?.can_access) load(); }, [meta?.can_access, load]);

  const restore = async (b) => {
    if (!window.confirm(
      `Restore ${b.entity_name} ka data?\n\n` +
      `${b.entries_soft_deleted} entries wapas aa jayengi.\n` +
      `Reset ke time inserted opening_balance entry (${fmt(b.opening_amount)}) hat jayegi.\n\n` +
      `Confirm karo?`
    )) return;
    try {
      const r = await api.post(`/data-reset/batches/${b.batch_id}/restore`);
      toast.success(r.data.message || 'Restored');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
  };

  if (!meta) return <div className="p-8 text-center text-sm text-gray-500">Loading...</div>;
  if (!meta.can_access) {
    return (
      <div className="max-w-md mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
        <Warning size={32} weight="duotone" className="mx-auto text-red-500 mb-2" />
        <div className="font-semibold text-red-900">Access Denied</div>
        <p className="text-sm text-red-700 mt-1">Admin se Data Reset access enable karwao. (Role: {user?.role})</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="data-recovery-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ArrowCounterClockwise size={26} weight="duotone" className="text-emerald-600" /> Data Recovery
          {meta.is_admin
            ? <span className="text-xs font-normal bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded ml-2">ADMIN</span>
            : <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-0.5 rounded ml-2">ACCESS GRANTED</span>}
        </h1>
        <div className="ml-auto flex gap-2">
          <Link to="/admin/data-reset">
            <Button size="sm" variant="outline" className="gap-1" data-testid="reset-link">
              ← Data Reset
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={load} className="gap-1">
            <ArrowClockwise size={14} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
        💡 Galti se kuch reset ho gya? Yahaan se 1-click restore kar sakte ho. 30 din tak retention rahta hai.
      </div>

      {/* Filter pills */}
      <div className="flex gap-1">
        {['all', 'active', 'restored'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 text-xs font-semibold rounded ${
              filter === f ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid={`filter-${f}`}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : batches.length === 0 ? <div className="p-12 text-center text-sm text-gray-500">No reset batches found.</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Reset Date</th>
                  <th className="px-3 py-2 text-left">Entity</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Entries Removed</th>
                  <th className="px-3 py-2 text-right">Opening Set</th>
                  <th className="px-3 py-2 text-left">By</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Expires</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const expired = b.expires_at && new Date(b.expires_at) < new Date();
                  return (
                    <tr key={b.batch_id} className="border-t hover:bg-gray-50" data-testid={`batch-row-${b.batch_id}`}>
                      <td className="px-3 py-2 text-xs">{fmtDate(b.reset_at)}</td>
                      <td className="px-3 py-2 font-semibold">{b.entity_name}</td>
                      <td className="px-3 py-2 text-xs">{TYPE_LABELS[b.entity_type] || b.entity_type}</td>
                      <td className="px-3 py-2 text-right text-red-700 font-semibold">{b.entries_soft_deleted}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="font-bold text-fuchsia-700">{fmt(b.opening_amount)}</div>
                        <div className="text-[10px] text-gray-500 uppercase">{b.opening_kind}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{b.reset_by_name}</td>
                      <td className="px-3 py-2">
                        {b.status === 'restored'
                          ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={11} weight="bold" /> RESTORED</span>
                          : expired
                          ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 inline-flex items-center gap-1"><Clock size={11} weight="bold" /> EXPIRED</span>
                          : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 inline-flex items-center gap-1"><Clock size={11} weight="bold" /> ACTIVE</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(b.expires_at)}</td>
                      <td className="px-3 py-2 text-right">
                        {b.status === 'active' && !expired ? (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 gap-1 h-7"
                            onClick={() => restore(b)}
                            data-testid={`restore-btn-${b.batch_id}`}
                          >
                            <ArrowCounterClockwise size={12} weight="bold" /> Restore
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
